// Draws a recipe as a 1080x1920 Instagram story image with the Lunelle logo, then opens the phone's share sheet (or downloads)
const W = 1080;
const H = 1920;
const PAD = 90;
const SERIF = "\"Cormorant Garamond\", Georgia, serif";
const SANS = "Manrope, Arial, sans-serif";

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const wrap = (ctx, text, maxW) => {
  const lines = [];
  let line = "";
  text.split(" ").forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; } else line = test;
  });
  if (line) lines.push(line);
  return lines;
};

// Lays everything out at scale s; returns the final y so we can shrink until it fits
const draw = (ctx, r, phaseName, why, logo, s, paint) => {
  const text = (str, font, color, x, y) => { if (paint) { ctx.font = font; ctx.fillStyle = color; ctx.fillText(str, x, y); } };
  const block = (str, size, font, color, x, y, maxW, lh = 1.3) => {
    ctx.font = `${font.replace("SIZE", String(Math.round(size)))}`;
    const lines = wrap(ctx, str, maxW);
    lines.forEach((l, i) => text(l, ctx.font, color, x, y + i * size * lh));
    return y + lines.length * size * lh;
  };
  let y = 150;
  if (paint && logo) ctx.drawImage(logo, PAD, y - 70, 80, 80);
  text("Lunelle", `400 58px ${SERIF}`, "#2C221E", PAD + 100, y);
  y += 90;
  text(`${phaseName.toUpperCase()} PLATE  ·  ${r.time.toUpperCase()}  ·  SERVES ${r.serves}`, `700 ${Math.round(26 * s)}px ${SANS}`, "#9E4429", PAD, y);
  y += 40 * s + 50 * s;
  y = block(r.title, 66 * s, `italic 500 SIZEpx ${SERIF}`, "#2C221E", PAD, y, W - PAD * 2, 1.12) + 30 * s;
  text("YOU'LL NEED", `700 ${Math.round(24 * s)}px ${SANS}`, "#7A665C", PAD, y);
  y += 46 * s;
  r.ingredients.forEach(({ item }) => {
    if (paint) { ctx.fillStyle = "#C86D51"; ctx.beginPath(); ctx.arc(PAD + 8, y - 10 * s, 6 * s, 0, Math.PI * 2); ctx.fill(); }
    y = block(item, 30 * s, `500 SIZEpx ${SANS}`, "#2C221E", PAD + 32, y, W - PAD * 2 - 32, 1.3) + 6 * s;
  });
  y += 30 * s;
  text("STEPS", `700 ${Math.round(24 * s)}px ${SANS}`, "#7A665C", PAD, y);
  y += 46 * s;
  r.steps.forEach((st, i) => {
    if (paint) {
      ctx.fillStyle = "#2C221E"; ctx.beginPath(); ctx.arc(PAD + 18, y - 10 * s, 20 * s, 0, Math.PI * 2); ctx.fill();
      ctx.font = `700 ${Math.round(22 * s)}px ${SANS}`; ctx.fillStyle = "#FBF5EE"; ctx.textAlign = "center"; ctx.fillText(String(i + 1), PAD + 18, y - 2 * s); ctx.textAlign = "left";
    }
    y = block(st, 29 * s, `400 SIZEpx ${SANS}`, "#3D302B", PAD + 60, y, W - PAD * 2 - 60, 1.35) + 14 * s;
  });
  y += 20 * s;
  y = block(`Why it helps: ${why}`, 28 * s, `italic 500 SIZEpx ${SERIF}`, "#9E4429", PAD, y + 10, W - PAD * 2, 1.3);
  return y;
};

export const makeRecipeStory = async (r, phaseName, why) => {
  await Promise.all([document.fonts?.load(`italic 500 60px "Cormorant Garamond"`), document.fonts?.load("500 30px Manrope"), document.fonts?.load("700 30px Manrope")]).catch(() => {});
  const logo = await loadImage("/press/lunelle-logo-mark.svg");
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  let s = 1;
  while (s > 0.55 && draw(ctx, r, phaseName, why, logo, s, false) > H - 200) s -= 0.04;
  ctx.fillStyle = "#FBF5EE";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W, 0, 50, W, 0, 900);
  glow.addColorStop(0, "rgba(232,185,154,0.55)");
  glow.addColorStop(1, "rgba(251,245,238,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  draw(ctx, r, phaseName, why, logo, s, true);
  ctx.fillStyle = "#2C221E";
  ctx.fillRect(0, H - 150, W, 150);
  ctx.font = `600 30px ${SANS}`;
  ctx.fillStyle = "#F4DED3";
  ctx.fillText("Eat for your phase with Lunelle", PAD, H - 88);
  ctx.font = `500 24px ${SANS}`;
  ctx.fillStyle = "#E8B99A";
  ctx.fillText(window.location.host, PAD, H - 46);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};

export const shareRecipeStory = async (r, phaseName, why) => {
  const blob = await makeRecipeStory(r, phaseName, why);
  const file = new File([blob], "lunelle-recipe-story.png", { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], title: r.title }); return "shared"; } catch { return "cancelled"; }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return "downloaded";
};
